import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IMAGE_URL } from "network";
import { useEffect, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

function AddForm({ open, onClose, initialData, onSubmit }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewUrl2, setPreviewUrl2] = useState("");
  const [previewUrl3, setPreviewUrl3] = useState("");
  const [previewUrl4, setPreviewUrl4] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      _id: initialData?._id || "",
      name: initialData?.name || "",
      image: initialData?.image || null, // ✅ use string if exists
      backgroundImage: initialData?.backgroundImage || null, // ✅ use string if exists
      appBackgroundImage: initialData?.appBackgroundImage || null, // ✅ use string if exists
      appIcon: initialData?.appIcon || null, // ✅ use string if exists
      metaTitle: initialData?.metaTitle || "",
      metaDescription: initialData?.metaDescription || "",
      keywords: initialData?.keywords || [],
      package: initialData?.package === "active" ? "active" : "inactive",
      module_type: initialData?.module_type || "independent",
      linked_url: initialData?.linked_url || "",
      description: initialData?.description || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Category name is required"),
      description: Yup.string(),
      metaTitle: Yup.string().required("Meta title is required"),
      metaDescription: Yup.string().required("Meta description is required"),
      linked_url: Yup.string().when("module_type", {
        is: (val) => val === "dependent",
        then: (schema) => schema.required("Linked URL is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      image: Yup.mixed()
        .nullable()
        .test("fileRequired", "Image is required", function (value) {
          return initialData?.image || value;
        }),
      appIcon: Yup.mixed()
        .nullable()
        .test("fileRequired", "App Icon is required", function (value) {
          return initialData?.appIcon || value;
        }),
      backgroundImage: Yup.mixed()
        .nullable()
        .test("bgRequired", "Background Image is required", function (value) {
          return initialData?.backgroundImage || value;
        }),
      appBackgroundImage: Yup.mixed()
        .nullable()
        .test(
          "bgRequired",
          "App Background Image is required",
          function (value) {
            return initialData?.appBackgroundImage || value;
          }
        ),
    }),
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  useEffect(() => {
    if (initialData?.image) {
      setPreviewUrl(IMAGE_URL + initialData.image);
    }
    if (initialData?.backgroundImage) {
      setPreviewUrl2(IMAGE_URL + initialData.backgroundImage);
    }
    if (initialData?.appIcon) {
      setPreviewUrl3(IMAGE_URL + initialData.appIcon);
    }
    if (initialData?.appBackgroundImage) {
      setPreviewUrl4(IMAGE_URL + initialData.appBackgroundImage);
    }
  }, [initialData]);

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formik.values.keywords.includes(trimmed)) {
      formik.setFieldValue("keywords", [...formik.values.keywords, trimmed]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw) => {
    formik.setFieldValue(
      "keywords",
      formik.values.keywords.filter((k) => k !== kw)
    );
  };

  const handleFileChange = (field, file) => {
    console.log("field", field);
    formik.setFieldValue(field, file);
    if (file) {
      const url = URL.createObjectURL(file);
      if (field === "image") {
        setPreviewUrl(url);
      } else if (field === "backgroundImage") {
        setPreviewUrl2(url);
      } else if (field === "appIcon") {
        setPreviewUrl3(url);
      } else if (field === "appBackgroundImage") {
        setPreviewUrl4(url);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initialData ? "Edit" : "Add"}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="module-type-label">Module Type</InputLabel>
            <Select
              labelId="module-type-label"
              name="module_type"
              value={formik.values.module_type}
              onChange={(e) => {
                const newValue = e.target.value;
                formik.setFieldValue("module_type", newValue);

                if (newValue === "dependent") {
                  formik.setTouched((prevTouched) => ({
                    ...prevTouched,
                    linked_url: true,
                  }));
                  formik.validateField("linked_url"); // ⬅️ Force validation
                } else {
                  formik.setTouched((prevTouched) => ({
                    ...prevTouched,
                    linked_url: false,
                  }));
                  formik.setFieldValue("linked_url", ""); // optionally clear it
                }
              }}
            >
              <MenuItem value="independent">Independent</MenuItem>
              <MenuItem value="dependent">Dependent</MenuItem>
            </Select>
          </FormControl>

          {formik.values.module_type === "dependent" && (
            <TextField
              name="linked_url"
              label="Linked URL"
              value={formik.values.linked_url}
              onChange={formik.handleChange}
              error={
                formik.touched.linked_url && Boolean(formik.errors.linked_url)
              }
              helperText={formik.touched.linked_url && formik.errors.linked_url}
              fullWidth
              margin="normal"
            />
          )}

          <TextField
            name="name"
            label="Category Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            margin="normal"
          />
          {/* Description */}
          <FormControl fullWidth margin="normal" required>
            <FormLabel>Description</FormLabel>
            <CKEditor
              editor={ClassicEditor}
              data={formik.values.description || ""} // ✅ safety guard
              config={{
                toolbar: [
                  "heading",
                  "|",
                  "bold",
                  "italic",
                  "underline",
                  "|",
                  "bulletedList",
                  "numberedList",
                  "|",
                  "insertTable",
                  "link",
                  "imageUpload",
                  "|",
                  "undo",
                  "redo",
                ],
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                  ],
                },
              }}
              onChange={(event, editor) => {
                formik.setFieldValue("description", editor.getData());
              }}
            />
          </FormControl>

          <Typography variant="h6">
            Background Image (width 1440px height 467px)
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              handleFileChange("backgroundImage", e.target.files[0])
            }
            style={{ marginTop: 16 }}
          />
          {formik.touched.backgroundImage && formik.errors.backgroundImage && (
            <Typography color="error" variant="caption">
              {formik.errors.backgroundImage}
            </Typography>
          )}
          {previewUrl2 && (
            <img
              crossOrigin="anonymous"
              src={previewUrl2}
              alt="Preview"
              style={{
                marginTop: 16,
                width: "100px",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}

          <Typography variant="h6">Image (width 111px height 102px)</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange("image", e.target.files[0])}
            style={{ marginTop: 16 }}
          />
          {formik.touched.image && formik.errors.image && (
            <Typography color="error" variant="caption">
              {formik.errors.image}
            </Typography>
          )}
          {previewUrl && (
            <img
              crossOrigin="anonymous"
              src={previewUrl}
              alt="Preview"
              style={{
                marginTop: 16,
                width: "100px",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}

          <Typography variant="h6">App Icon</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange("appIcon", e.target.files[0])}
            style={{ marginTop: 16 }}
          />
          {formik.touched.appIcon && formik.errors.appIcon && (
            <Typography color="error" variant="caption">
              {formik.errors.appIcon}
            </Typography>
          )}
          {previewUrl3 && (
            <img
              crossOrigin="anonymous"
              src={previewUrl3}
              alt="Preview"
              style={{
                marginTop: 16,
                width: "100px",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}
          <Typography variant="h6">App Background Image</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              handleFileChange("appBackgroundImage", e.target.files[0])
            }
            style={{ marginTop: 16 }}
          />
          {formik.touched.appBackgroundImage &&
            formik.errors.appBackgroundImage && (
              <Typography color="error" variant="caption">
                {formik.errors.appBackgroundImage}
              </Typography>
            )}
          {previewUrl4 && (
            <img
              crossOrigin="anonymous"
              src={previewUrl4}
              alt="Preview"
              style={{
                marginTop: 16,
                width: "100px",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}

          <Typography variant="h6" sx={{ mt: 2 }}>
            Include Packages
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.package === "active"}
                onChange={(e) =>
                  formik.setFieldValue(
                    "package",
                    e.target.checked ? "active" : "inactive"
                  )
                }
              />
            }
            label="Do you want to include Packages?"
          />

          <TextField
            name="metaTitle"
            label="Meta Title"
            value={formik.values.metaTitle}
            onChange={formik.handleChange}
            error={formik.touched.metaTitle && Boolean(formik.errors.metaTitle)}
            helperText={formik.touched.metaTitle && formik.errors.metaTitle}
            fullWidth
            margin="normal"
          />

          <TextField
            name="metaDescription"
            label="Meta Description"
            value={formik.values.metaDescription}
            onChange={formik.handleChange}
            error={
              formik.touched.metaDescription &&
              Boolean(formik.errors.metaDescription)
            }
            helperText={
              formik.touched.metaDescription && formik.errors.metaDescription
            }
            fullWidth
            margin="normal"
          />

          <Box display="flex" alignItems="center" mt={2} mb={1} gap={1}>
            <TextField
              label="Add Keyword"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addKeyword();
                }
              }}
              fullWidth
            />
            <Button onClick={addKeyword} variant="contained" color="primary">
              Add
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {formik.values?.keywords?.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                onDelete={() => removeKeyword(keyword)}
                color="primary"
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained">
            {initialData ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddForm;
